import { useEffect, useState } from 'react';
import { auditLogApi, userApi } from '../../api';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

export default function UserManagementAuditLog() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({ userId: '', entity: '', action: '', dateFrom: '', dateTo: '' });

  const load = async () => {
    setError('');
    const params = { module: 'user_management' };
    if (filter.userId) params.userId = filter.userId;
    if (filter.entity) params.entity = filter.entity;
    if (filter.action) params.action = filter.action;
    if (filter.dateFrom) params.dateFrom = filter.dateFrom;
    if (filter.dateTo) params.dateTo = filter.dateTo;
    try {
      const [l, u] = await Promise.all([auditLogApi.list(params), userApi.list()]);
      setLogs(l.data.data);
      setUsers(u.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const actionColor = { CREATE: 'text-green-700', UPDATE: 'text-blue-700', DELETE: 'text-red-700', BULK_UPLOAD: 'text-purple-700' };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="User Management Audit Logs" subtitle="Account creation, updates, deletions, role and permission changes — visible to Super Admin only" />

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      <div className="flex gap-3 mb-4 flex-wrap">
        <select className="input w-40" value={filter.userId} onChange={(e) => setFilter({ ...filter, userId: e.target.value })}>
          <option value="">All Users</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
        </select>
        <select className="input w-44" value={filter.entity} onChange={(e) => setFilter({ ...filter, entity: e.target.value })}>
          <option value="">All Entities</option>
          {['user', 'permission_group'].map((e) => (
            <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select className="input w-36" value={filter.action} onChange={(e) => setFilter({ ...filter, action: e.target.value })}>
          <option value="">All Actions</option>
          {['CREATE', 'UPDATE', 'DELETE', 'BULK_UPLOAD'].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input className="input w-40" type="date" value={filter.dateFrom} onChange={(e) => setFilter({ ...filter, dateFrom: e.target.value })} />
        <input className="input w-40" type="date" value={filter.dateTo} onChange={(e) => setFilter({ ...filter, dateTo: e.target.value })} />
      </div>

      <div className="table-container">
        <table>
          <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Entity</th><th>Name</th><th>IP</th></tr></thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="text-xs text-gray-500">{new Date(log.createdAt).toLocaleString()}</td>
                <td className="text-sm">{log.user?.username || <span className="text-gray-400">system</span>}</td>
                <td><span className={`text-xs font-semibold uppercase ${actionColor[log.action] || 'text-gray-700'}`}>{log.action}</span></td>
                <td className="text-sm text-gray-600 capitalize">{log.entity?.replace(/_/g, ' ')}</td>
                <td className="text-sm">{log.entityName || '—'}</td>
                <td className="text-xs text-gray-400">{log.ipAddress || '—'}</td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-gray-400">No audit log entries found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
